<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import OpenHouseAPI from 'dashboard/api/openHouse';
import OpenHouseRegistrationRow from '../components/OpenHouseRegistrationRow.vue';
import { presentEvent, eventStatusTone } from '../utils/openHousePresenter';

const route = useRoute();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');

const isLoading = ref(true);
const hasError = ref(false);
const hasPermissionError = ref(false);
const rawEvent = ref(null);
const lifecycleInFlight = ref('');
const registrationInFlight = ref('');
const registerLoading = ref(false);
const walkInLoading = ref(false);
const checkinLoading = ref(false);
const actionError = ref('');
const checkinToken = ref('');
const checkinFeedback = ref({ tone: '', message: '' });
const registerFeedback = ref({ tone: '', message: '' });
const walkInFeedback = ref({ tone: '', message: '' });
const conversionTarget = ref('private_visit');
const registerForm = ref({
  contact_id: '',
  companions: 0,
});
const walkInForm = ref({
  contact_id: '',
  companions: 0,
});

const canRevealSecret = computed(() => currentRole.value === 'administrator');

const event = computed(() =>
  rawEvent.value
    ? presentEvent(rawEvent.value, { canRevealSecret: canRevealSecret.value })
    : null
);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  hasPermissionError.value = false;
  try {
    const { data } = await OpenHouseAPI.show(route.params.id);
    rawEvent.value = data?.payload || null;
  } catch (error) {
    if (error?.response?.status === 403) hasPermissionError.value = true;
    else hasError.value = true;
    rawEvent.value = null;
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const backTo = computed(() =>
  frontendURL(`accounts/${accountId.value}/open-house`)
);

const LIFECYCLE_META = {
  schedule: {
    icon: 'i-lucide-calendar-check',
  },
  open: {
    icon: 'i-lucide-door-open',
  },
  close: {
    icon: 'i-lucide-door-closed',
  },
  cancel: { icon: 'i-lucide-x' },
};

const conversionTargets = computed(() => [
  {
    value: 'property_interest',
    label: t('VISITS.OPEN_HOUSE.CONVERSION_TARGETS.PROPERTY_INTEREST'),
  },
  { value: 'demand', label: t('VISITS.OPEN_HOUSE.CONVERSION_TARGETS.DEMAND') },
  { value: 'task', label: t('VISITS.OPEN_HOUSE.CONVERSION_TARGETS.TASK') },
  {
    value: 'private_visit',
    label: t('VISITS.OPEN_HOUSE.CONVERSION_TARGETS.PRIVATE_VISIT'),
  },
  { value: 'offer', label: t('VISITS.OPEN_HOUSE.CONVERSION_TARGETS.OFFER') },
]);

const lifecycleLabel = action => {
  if (action === 'schedule') return t('VISITS.OPEN_HOUSE.LIFECYCLE.SCHEDULE');
  if (action === 'open') return t('VISITS.OPEN_HOUSE.LIFECYCLE.OPEN');
  if (action === 'close') return t('VISITS.OPEN_HOUSE.LIFECYCLE.CLOSE');
  return t('VISITS.OPEN_HOUSE.LIFECYCLE.CANCEL');
};

const eventStatusLabel = status => {
  if (status === 'scheduled') return t('VISITS.OPEN_HOUSE.STATUS.SCHEDULED');
  if (status === 'open') return t('VISITS.OPEN_HOUSE.STATUS.OPEN');
  if (status === 'closed') return t('VISITS.OPEN_HOUSE.STATUS.CLOSED');
  if (status === 'canceled') return t('VISITS.OPEN_HOUSE.STATUS.CANCELED');
  return t('VISITS.OPEN_HOUSE.STATUS.DRAFT');
};

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('VISITS.OPEN_HOUSE.ACTION_ERROR');

const setFeedback = (target, tone, message) => {
  target.value = { tone, message };
};

const runLifecycle = async action => {
  lifecycleInFlight.value = action;
  actionError.value = '';
  try {
    if (action === 'schedule') await OpenHouseAPI.schedule(event.value.id);
    else if (action === 'open') await OpenHouseAPI.open(event.value.id);
    else if (action === 'close') await OpenHouseAPI.close(event.value.id);
    else if (action === 'cancel') await OpenHouseAPI.cancel(event.value.id, {});
    await load();
  } catch (error) {
    actionError.value = errorMessage(error);
  } finally {
    lifecycleInFlight.value = '';
  }
};

const lifecycleActions = computed(() => {
  if (!event.value) return [];
  return event.value.lifecycleActions.map(action => ({
    label: lifecycleLabel(action),
    icon: LIFECYCLE_META[action].icon,
    loading: lifecycleInFlight.value === action,
    onClick: () => runLifecycle(action),
  }));
});

const runRegistrationAction = async ({ action, registration }) => {
  registrationInFlight.value = `${registration.id}:${action}`;
  actionError.value = '';
  try {
    if (action === 'confirm') {
      await OpenHouseAPI.confirmRegistration(event.value.id, registration.id);
    } else if (action === 'check_in') {
      await OpenHouseAPI.checkIn(event.value.id, {
        registration_id: registration.id,
        kind: 'manual',
      });
    } else if (action === 'convert') {
      await OpenHouseAPI.convert(event.value.id, {
        registration_id: registration.id,
        into: conversionTarget.value,
      });
    } else if (action === 'no_show') {
      await OpenHouseAPI.noShowRegistration(event.value.id, registration.id);
    } else if (action === 'cancel') {
      await OpenHouseAPI.cancelRegistration(
        event.value.id,
        registration.id,
        {}
      );
    }
    await load();
  } catch (error) {
    actionError.value = errorMessage(error);
  } finally {
    registrationInFlight.value = '';
  }
};

const promoteWaitlist = async () => {
  registrationInFlight.value = 'promote';
  actionError.value = '';
  try {
    await OpenHouseAPI.promote(event.value.id);
    await load();
  } catch (error) {
    actionError.value = errorMessage(error);
  } finally {
    registrationInFlight.value = '';
  }
};

const submitRegistration = async () => {
  if (!registerForm.value.contact_id || registerLoading.value) return;
  setFeedback(registerFeedback, '', '');
  registerLoading.value = true;
  try {
    await OpenHouseAPI.register(event.value.id, {
      contact_id: Number(registerForm.value.contact_id),
      companions: Number(registerForm.value.companions || 0),
      origin: 'agent',
    });
    registerForm.value = { contact_id: '', companions: 0 };
    setFeedback(
      registerFeedback,
      'positive',
      t('VISITS.OPEN_HOUSE.REGISTER.SUCCESS')
    );
    await load();
  } catch (error) {
    setFeedback(registerFeedback, 'danger', errorMessage(error));
  } finally {
    registerLoading.value = false;
  }
};

const submitWalkIn = async () => {
  if (!walkInForm.value.contact_id || walkInLoading.value) return;
  setFeedback(walkInFeedback, '', '');
  walkInLoading.value = true;
  try {
    const { data } = await OpenHouseAPI.walkIn(event.value.id, {
      contact_id: Number(walkInForm.value.contact_id),
      companions: Number(walkInForm.value.companions || 0),
      origin: 'walk_in',
    });
    walkInForm.value = { contact_id: '', companions: 0 };
    setFeedback(
      walkInFeedback,
      'positive',
      data?.payload?.checkin_id
        ? t('VISITS.OPEN_HOUSE.WALK_IN.SUCCESS')
        : t('VISITS.OPEN_HOUSE.WALK_IN.WAITLISTED')
    );
    await load();
  } catch (error) {
    setFeedback(walkInFeedback, 'danger', errorMessage(error));
  } finally {
    walkInLoading.value = false;
  }
};

const submitCheckin = async () => {
  if (!checkinToken.value.trim() || checkinLoading.value) return;
  checkinFeedback.value = { tone: '', message: '' };
  checkinLoading.value = true;
  try {
    const { data } = await OpenHouseAPI.checkIn(event.value.id, {
      access_token: checkinToken.value.trim(),
      kind: 'scan',
    });
    checkinFeedback.value = {
      tone: 'positive',
      message: data?.payload?.replayed
        ? t('VISITS.OPEN_HOUSE.CHECKIN.REPLAYED')
        : t('VISITS.OPEN_HOUSE.CHECKIN.SUCCESS'),
    };
    checkinToken.value = '';
    await load();
  } catch (error) {
    checkinFeedback.value = {
      tone: 'danger',
      message: errorMessage(error) || t('VISITS.OPEN_HOUSE.CHECKIN.ERROR'),
    };
  } finally {
    checkinLoading.value = false;
  }
};

const waitlistCount = computed(
  () =>
    event.value?.registrations.filter(reg => reg.status === 'waitlisted')
      .length || 0
);

const conversionCount = computed(
  () =>
    event.value?.registrations.reduce(
      (total, registration) => total + registration.conversions.length,
      0
    ) || 0
);
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg md:overflow-hidden"
  >
    <LaisHeader
      sticky
      show-back
      :back-label="t('VISITS.OPEN_HOUSE.BACK')"
      :title="t('VISITS.OPEN_HOUSE.DETAIL_TITLE')"
      @back="$router.push(backTo)"
    />

    <div
      class="min-h-0 flex-1 overflow-auto px-4 pb-28 pt-4 sm:px-6 sm:pb-8 lg:px-8 max-md:flex-none max-md:overflow-visible"
    >
      <div class="mx-auto flex w-full max-w-4xl flex-col gap-5">
        <div v-if="isLoading" class="grid gap-4" aria-busy="true" role="status">
          <div class="h-24 w-full animate-pulse rounded-lais bg-lais-bg-soft" />
          <div class="h-64 w-full animate-pulse rounded-lais bg-lais-bg-soft" />
        </div>

        <LaisErrorState
          v-else-if="hasPermissionError"
          status="403"
          icon="i-lucide-lock"
          :title="t('VISITS.OPEN_HOUSE.PERMISSION_TITLE')"
          :description="t('VISITS.OPEN_HOUSE.PERMISSION_DESCRIPTION')"
        />

        <LaisErrorState
          v-else-if="hasError"
          :title="t('VISITS.OPEN_HOUSE.ERROR_TITLE')"
          :description="t('VISITS.OPEN_HOUSE.ERROR_DESCRIPTION')"
          show-action
          :action-label="t('VISITS.OPEN_HOUSE.RETRY')"
          @action="load"
        />

        <template v-else-if="event">
          <section
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <LaisStatusBadge
                :label="eventStatusLabel(event.status)"
                :tone="eventStatusTone(event.status)"
              />
              <span class="text-lais-body-sm tabular-nums text-lais-text-body">
                <template v-if="event.capacity.unlimited">
                  {{ t('VISITS.OPEN_HOUSE.CAPACITY_UNLIMITED') }}
                </template>
                <template v-else>
                  {{
                    t('VISITS.OPEN_HOUSE.CAPACITY_COUNT', {
                      occupied: event.capacity.occupied,
                      capacity: event.capacity.capacity,
                    })
                  }}
                </template>
              </span>
            </div>
            <h1
              class="truncate font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
            >
              {{ event.title || t('VISITS.OPEN_HOUSE.UNTITLED') }}
            </h1>
            <div
              class="flex flex-wrap gap-x-6 gap-y-1 text-lais-body-sm text-lais-text-muted"
            >
              <span v-if="event.startsAt">
                {{
                  new Date(event.startsAt).toLocaleString('pt-BR', {
                    timeZone: 'UTC',
                  })
                }}
              </span>
              <span>{{
                t('VISITS.OPEN_HOUSE.CHECKED_IN', {
                  count: event.capacity.checkedIn,
                })
              }}</span>
              <span v-if="waitlistCount">{{
                t('VISITS.OPEN_HOUSE.WAITLISTED', { count: waitlistCount })
              }}</span>
              <span v-if="conversionCount">{{
                t('VISITS.OPEN_HOUSE.CONVERTED', { count: conversionCount })
              }}</span>
            </div>
          </section>

          <p
            v-if="actionError"
            class="rounded-lais border border-lais-danger/30 bg-lais-danger-50 px-4 py-3 text-lais-body-sm text-lais-danger"
            role="alert"
          >
            {{ actionError }}
          </p>

          <section
            v-if="event.registrable"
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <h2
              class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              <span
                class="i-lucide-user-plus size-5 shrink-0 text-lais-primary"
                aria-hidden="true"
              />
              {{ t('VISITS.OPEN_HOUSE.REGISTER.TITLE') }}
            </h2>
            <form
              class="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_8rem_auto] sm:items-end"
              @submit.prevent="submitRegistration"
            >
              <LaisField
                :label="t('VISITS.OPEN_HOUSE.REGISTER.CONTACT_ID')"
                required
              >
                <input
                  v-model="registerForm.contact_id"
                  type="number"
                  min="1"
                  class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                />
              </LaisField>
              <LaisField :label="t('VISITS.OPEN_HOUSE.REGISTER.COMPANIONS')">
                <input
                  v-model="registerForm.companions"
                  type="number"
                  min="0"
                  class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                />
              </LaisField>
              <LaisPrimaryButton
                type="submit"
                :disabled="!registerForm.contact_id"
                :is-loading="registerLoading"
              >
                {{ t('VISITS.OPEN_HOUSE.REGISTER.SUBMIT') }}
              </LaisPrimaryButton>
            </form>
            <p
              v-if="registerFeedback.message"
              class="text-lais-body-sm"
              :class="
                registerFeedback.tone === 'danger'
                  ? 'text-lais-danger'
                  : 'text-lais-mint-dark'
              "
              role="status"
            >
              {{ registerFeedback.message }}
            </p>
          </section>

          <section
            v-if="event.checkinable"
            class="flex flex-col gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <h2
              class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              <span
                class="i-lucide-qr-code size-5 shrink-0 text-lais-primary"
                aria-hidden="true"
              />
              {{ t('VISITS.OPEN_HOUSE.CHECKIN.TITLE') }}
            </h2>
            <form
              class="flex flex-col gap-2 sm:flex-row sm:items-end"
              @submit.prevent="submitCheckin"
            >
              <LaisField
                :label="t('VISITS.OPEN_HOUSE.CHECKIN.TOKEN')"
                class="flex-1"
              >
                <input
                  v-model="checkinToken"
                  type="text"
                  autocomplete="off"
                  class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                />
              </LaisField>
              <LaisPrimaryButton
                type="submit"
                :disabled="!checkinToken.trim()"
                :is-loading="checkinLoading"
              >
                {{ t('VISITS.OPEN_HOUSE.CHECKIN.SUBMIT') }}
              </LaisPrimaryButton>
            </form>
            <p
              v-if="checkinFeedback.message"
              class="text-lais-body-sm"
              :class="
                checkinFeedback.tone === 'danger'
                  ? 'text-lais-danger'
                  : 'text-lais-mint-dark'
              "
              role="status"
            >
              {{ checkinFeedback.message }}
            </p>

            <form
              class="grid grid-cols-1 gap-3 border-t border-lais-border-soft pt-4 sm:grid-cols-[minmax(0,1fr)_8rem_auto] sm:items-end"
              @submit.prevent="submitWalkIn"
            >
              <LaisField
                :label="t('VISITS.OPEN_HOUSE.WALK_IN.CONTACT_ID')"
                required
              >
                <input
                  v-model="walkInForm.contact_id"
                  type="number"
                  min="1"
                  class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                />
              </LaisField>
              <LaisField :label="t('VISITS.OPEN_HOUSE.WALK_IN.COMPANIONS')">
                <input
                  v-model="walkInForm.companions"
                  type="number"
                  min="0"
                  class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                />
              </LaisField>
              <LaisPrimaryButton
                type="submit"
                variant="ghost"
                :disabled="!walkInForm.contact_id"
                :is-loading="walkInLoading"
              >
                {{ t('VISITS.OPEN_HOUSE.WALK_IN.SUBMIT') }}
              </LaisPrimaryButton>
            </form>
            <p
              v-if="walkInFeedback.message"
              class="text-lais-body-sm"
              :class="
                walkInFeedback.tone === 'danger'
                  ? 'text-lais-danger'
                  : 'text-lais-mint-dark'
              "
              role="status"
            >
              {{ walkInFeedback.message }}
            </p>
          </section>

          <section
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <div class="flex flex-wrap items-end justify-between gap-3">
              <h2
                class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
              >
                <span
                  class="i-lucide-users size-5 shrink-0 text-lais-primary"
                  aria-hidden="true"
                />
                {{ t('VISITS.OPEN_HOUSE.ROSTER') }}
                <span class="tabular-nums text-lais-text-muted">
                  {{
                    t('VISITS.OPEN_HOUSE.ROSTER_COUNT', {
                      count: event.registrations.length,
                    })
                  }}
                </span>
              </h2>
              <div class="flex flex-wrap items-end gap-2">
                <LaisField
                  :label="t('VISITS.OPEN_HOUSE.CONVERSION_TARGET')"
                  class="w-56"
                >
                  <select
                    v-model="conversionTarget"
                    class="h-9 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                  >
                    <option
                      v-for="target in conversionTargets"
                      :key="target.value"
                      :value="target.value"
                    >
                      {{ target.label }}
                    </option>
                  </select>
                </LaisField>
                <LaisPrimaryButton
                  v-if="waitlistCount && !event.terminal"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-arrow-up-narrow-wide"
                  :is-loading="registrationInFlight === 'promote'"
                  @click="promoteWaitlist"
                >
                  {{ t('VISITS.OPEN_HOUSE.PROMOTE_WAITLIST') }}
                </LaisPrimaryButton>
              </div>
            </div>

            <p
              v-if="!event.registrations.length"
              class="rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-6 text-center text-lais-body-sm text-lais-text-muted"
            >
              {{ t('VISITS.OPEN_HOUSE.ROSTER_EMPTY') }}
            </p>
            <ul
              v-else
              class="divide-y divide-lais-border-soft rounded-lais border border-lais-border-soft"
              role="list"
            >
              <OpenHouseRegistrationRow
                v-for="registration in event.registrations"
                :key="registration.id"
                :registration="registration"
                :action-in-flight="registrationInFlight"
                @action="runRegistrationAction"
              />
            </ul>
          </section>

          <section
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <h2
              class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              <span
                class="i-lucide-clipboard-check size-5 shrink-0 text-lais-primary"
                aria-hidden="true"
              />
              {{ t('VISITS.OPEN_HOUSE.CHECKINS.TITLE') }}
              <span class="tabular-nums text-lais-text-muted">
                {{
                  t('VISITS.OPEN_HOUSE.CHECKINS.COUNT', {
                    count: event.checkins.length,
                  })
                }}
              </span>
            </h2>
            <p
              v-if="!event.checkins.length"
              class="rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-6 text-center text-lais-body-sm text-lais-text-muted"
            >
              {{ t('VISITS.OPEN_HOUSE.CHECKINS.EMPTY') }}
            </p>
            <ul v-else class="grid gap-2" role="list">
              <li
                v-for="checkin in event.checkins"
                :key="checkin.id"
                class="flex flex-wrap items-center justify-between gap-2 rounded-lais border border-lais-border-soft px-3 py-2 text-lais-body-sm"
              >
                <span class="font-semibold text-lais-text">
                  {{
                    t('VISITS.OPEN_HOUSE.CHECKINS.REGISTRATION', {
                      id: checkin.registrationId,
                    })
                  }}
                </span>
                <span class="text-lais-text-muted">
                  {{
                    new Date(checkin.checkedInAt).toLocaleString('pt-BR', {
                      timeZone: 'UTC',
                    })
                  }}
                </span>
              </li>
            </ul>
          </section>

          <section
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
          >
            <h2
              class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              <span
                class="i-lucide-activity size-5 shrink-0 text-lais-primary"
                aria-hidden="true"
              />
              {{ t('VISITS.OPEN_HOUSE.ACTIVITY.TITLE') }}
            </h2>
            <p
              v-if="!event.activityEvents.length"
              class="rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-6 text-center text-lais-body-sm text-lais-text-muted"
            >
              {{ t('VISITS.OPEN_HOUSE.ACTIVITY.EMPTY') }}
            </p>
            <ol v-else class="grid gap-2" role="list">
              <li
                v-for="activity in event.activityEvents"
                :key="activity.id"
                class="rounded-lais border border-lais-border-soft px-3 py-2"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <span class="text-lais-label-sm font-semibold text-lais-text">
                    {{ activity.name }}
                  </span>
                  <span class="text-lais-label-sm text-lais-text-muted">
                    {{
                      new Date(activity.occurredAt).toLocaleString('pt-BR', {
                        timeZone: 'UTC',
                      })
                    }}
                  </span>
                </div>
              </li>
            </ol>
          </section>
        </template>
      </div>
    </div>

    <LaisActionBar
      v-if="event && lifecycleActions.length"
      sticky-mobile
      wrap-mobile
      :actions="lifecycleActions"
      :aria-label="t('VISITS.OPEN_HOUSE.LIFECYCLE.LABEL')"
    />
  </div>
</template>
